import {
  faCircleCheck,
  faExclamationCircle,
  faTimesCircle,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { isFragmentRef } from "@skydio/relay-toolkit";
import { Tag } from "@skydio/rivet-ui";

import type { ComponentPropsWithoutRef } from "react";
import type { UnpackedFragmentData } from "@skydio/relay-toolkit";
import type { ForwardRefComponentWithSubcomponents, TagProps } from "@skydio/rivet-ui";
import type {
  DeviceHealthStatusTag_device$data,
  DeviceHealthStatusTag_device$key,
} from "../../__generated__/DeviceHealthStatusTag_device.graphql";

const Fragment = graphql`
  fragment DeviceHealthStatusTag_device on Device {
    deviceHealthStatus
  }
`;

type BaseProps = ComponentPropsWithoutRef<"div"> & {
  showIcon?: boolean;
  text?: string;
};

export type DeviceHealthStatusTagFragment = DeviceHealthStatusTag_device$key;
export type DeviceHealthStatusTagData = UnpackedFragmentData<DeviceHealthStatusTag_device$data>;

interface InnerProps extends BaseProps {
  deviceData: DeviceHealthStatusTagData;
  grayHealthStatus: boolean;
}

const getHealthStatusTagProperties = (
  status: DeviceHealthStatusTagData["deviceHealthStatus"] | null,
  grayHealthStatus: boolean
): {
  color: TagProps["color"];
  icon?: React.ReactNode;
  text: string;
} => {
  switch (status) {
    case "GREEN":
      return {
        color: grayHealthStatus ? "neutral" : "success",
        icon: <FontAwesomeIcon icon={faCircleCheck} />,
        text: grayHealthStatus ? "No Known Issues" : "Healthy",
      };
    case "YELLOW":
      return {
        color: "warning",
        icon: <FontAwesomeIcon icon={faExclamationCircle} />,
        text: "Limited Operation",
      };
    case "RED":
      return {
        color: "danger",
        icon: <FontAwesomeIcon icon={faTimesCircle} />,
        text: "Inoperable",
      };
    default:
      return {
        color: "neutral",
        text: "Unknown",
      };
  }
};

const DeviceHealthStatusTagInner = forwardRef<HTMLDivElement, InnerProps>(
  ({ deviceData, showIcon, text: overrideText, grayHealthStatus, ...props }, ref) => {
    const { color, icon, text } = getHealthStatusTagProperties(
      deviceData.deviceHealthStatus,
      grayHealthStatus
    );

    return (
      <Tag size="sm" {...props} color={color} ref={ref}>
        <div className="flex flex-row items-center gap-1">
          {showIcon && icon}
          {overrideText ?? text}
        </div>
      </Tag>
    );
  }
);
DeviceHealthStatusTagInner.displayName = "DeviceHealthStatusTag.Inner";

/*************** WRAPPER COMPONENT BOILERPLATE BELOW - DO NOT MODIFY **************/

interface WrapperProps extends BaseProps {
  deviceRef: DeviceHealthStatusTagFragment;
}

const DeviceHealthStatusTagWrapper = forwardRef<HTMLDivElement, WrapperProps>(
  ({ deviceRef, ...props }, ref) => {
    const deviceData = useFragment(Fragment, deviceRef);
    return (
      <DeviceHealthStatusTagInner deviceData={deviceData} grayHealthStatus {...props} ref={ref} />
    );
  }
);
DeviceHealthStatusTagWrapper.displayName = "DeviceHealthStatusTag.Wrapper";

export type DeviceHealthStatusTagProps = BaseProps & {
  device: DeviceHealthStatusTagData | DeviceHealthStatusTagFragment;
};

export const DeviceHealthStatusTag = forwardRef<HTMLDivElement, DeviceHealthStatusTagProps>(
  ({ device, ...props }, ref) => {
    if (isFragmentRef(device, "DeviceHealthStatusTag_device")) {
      return <DeviceHealthStatusTagWrapper deviceRef={device} {...props} ref={ref} />;
    }
    return <DeviceHealthStatusTagInner deviceData={device} grayHealthStatus {...props} ref={ref} />;
  }
) as ForwardRefComponentWithSubcomponents<
  HTMLDivElement,
  {
    Inner: typeof DeviceHealthStatusTagInner;
    Wrapper: typeof DeviceHealthStatusTagWrapper;
  },
  DeviceHealthStatusTagProps
>;
DeviceHealthStatusTag.displayName = "DeviceHealthStatusTag";
